<script lang="ts">
import deepEqual from 'fast-deep-equal';
import { createEventDispatcher } from 'svelte';
import type { CourseListItem } from '../api/types';
import courses from '../store/courses';

import FileList from './FileList.svelte';
import Input from './Input.svelte';
import Button from './Button.svelte';
import Checkbox from './Checkbox.svelte';
import EmailPreview from './EmailPreview.svelte';
import type { EmailTemplate } from './EmailPreview.svelte';
import SendTestEmail from './SendTestEmail.svelte';

export let course: CourseListItem;
let form: CourseListItem;
let prev: CourseListItem;
let updated = false;
let locked = false;
const dispatch = createEventDispatcher();

const email: EmailTemplate[] = [
    ['Появился новый курс', 'new_course'],
    ['Пользователь зарегистрировался на курс', 'new_course_register'],
    ['Завершилось 2 лекции курса', 'lecture_finished2'],
    ['Благодарность за покупку курса', 'thanks'],
    ['Список всех видео лекций', 'video_all'],
]

$: clone(course);
$: updated = !deepEqual(form, course);

function clone(data: CourseListItem): void {
    // Обходим слишком умную систему Svelte построения зависимостей для инвалидации.
    // Логичнее было бы на уровне компонента написать
    // $: form = clone(course)
    // Однако в этом случае Svelte зачем-то добавит в биндингах инвалидацию
    // объекта `course`, что в итоге приведёт к новому циклу обновления `form`
    // и перетрёт данные.
    // В такой реализации инвалидация будет работать правильно
    if (prev !== data) {
        form = {
            ...data,
            files: [],
            fullCoursePrice: {
                ...data.fullCoursePrice
            },
            mailCampaign: {
                ...data.mailCampaign
            }
        };
        prev = data;
    }
}

function onSubmit() {
    if (!locked) {
        dispatch('submit', form);
    }
}

function onReset() {
    clone(course);
}

function onUploadEnd(evt: CustomEvent) {
    locked = false;
    form.files = evt.detail;
}

function onDelete(evt: CustomEvent) {
    form.files = evt.detail;
}
</script>
<style lang="scss">
form {
    > :global(.input), fieldset {
        margin-top: 24px;
    }
}

fieldset {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 10px;
    border: 0;
    padding: 0;
    margin: 24px 0;
}
</style>
<form action="/" on:submit|preventDefault={onSubmit} on:reset|preventDefault={onReset}>
    <Input name="title" bind:value={form.title}>Название курса</Input>
    <Input name="courseDescription" type="html" bind:value={form.courseDescription}>Описание курса</Input>
    <fieldset>
        <Input name="dateStart" type="date" bind:value={form.dateStart}>Дата начала</Input>
        <Input name="dateEnd" type="date" bind:value={form.dateEnd}>Дата завершения</Input>
        <Input name="courseTag" bind:value={form.courseTag}>Тэг</Input>
    </fieldset>

    <fieldset>
        <Input name="age" bind:value={form.age}>Возрастное ограничение</Input>
        <Input name="datesDescription" bind:value={form.datesDescription}>Комментарий по лекциям</Input>
        <Input name="videoUrl" bind:value={form.videoUrl}>Видео-приглашение</Input>
    </fieldset>

    <fieldset>
        <Input name="fullCoursePrice" type="number" bind:value={form.fullCoursePrice.amount}>Цена, ₽</Input>
        <Input name="promoCode" bind:value={form.promoCode}>Промо-код</Input>
    </fieldset>
    <hr />

    <fieldset>
        <Input name="registerButtonText" bind:value={form.registerButtonText} placeholder="Зарегистрироваться">Текст основной кнопки</Input>
        <Input name="headerFontSize" type="number" bind:value={form.headerFontSize} placeholder="40">Размер заголовка</Input>
        <Input name="externalLink" bind:value={form.externalLink}>Ссылка с картинки</Input>
    </fieldset>

    <fieldset>
        <Input name="subject" bind:value={form.mailCampaign.subject}>Тема письма</Input>
        <Input name="scheduleDate" type="datetime" bind:value={form.mailCampaign.scheduleDate}>Когда отправить</Input>
    </fieldset>

    <Checkbox name="sendEmail" bind:checked={form.mailCampaign.ready}>Отправлять письмо</Checkbox>

    <hr />

    <h2>Обложки и файлы по курсу</h2>
    <FileList files={course.files}
        on:upload-start={() => locked = true}
        on:upload-end={onUploadEnd}
        on:delete={onDelete} />

    <SendTestEmail id={course.id} />
    <EmailPreview items={email} courseId={course.id} />

    <footer>
        <Button type="reset">Сбросить</Button>
        <Button type="submit" role="primary" disabled={$courses.progress === 'course' ? 'progress' : (!locked && !updated)}>Сохранить данные</Button>
    </footer>
</form>
